import { useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, ExternalLink, FileText, FileUp, RefreshCw, Upload } from 'lucide-react'
import {
  Button,
  Chip,
  DataTable,
  Modal,
  PageHeaderBand,
  QueryErrorState,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  toast,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import { DOC_REQUEST_CREATE_ROLES } from '@/shell/services/regulatory/api/doc-requests'
import { RequestDocsDrawer } from '@/shell/services/regulatory/components/RequestDocsDrawer'
import { documentUrl } from '@/shell/services/cofa-registry/utils/document-url'
import { odooProductTemplateUrl } from '@/lib/utils/odoo-url'
import { useArchiveProductDocument, useDocumentTypes, useProductDocuments } from '../api/queries'
import type { ProductDocument } from '../api/types'
import { ExpiryChip } from '../components/ExpiryChip'
import { PanelCard } from '@/shell/forms/FormKit'
import { UploadDocumentDrawer } from '../components/UploadDocumentDrawer'
import { formatDate } from '../utils/dates'
import { documentChips } from '../utils/section-status'
import { UPLOAD_ROLES } from '../utils/permissions'

const STATUS_LABEL: Record<ProductDocument['status'], string> = {
  current: 'Current',
  superseded: 'Superseded',
  archived: 'Withdrawn',
}

/**
 * Regulatory Document Library for one product. Files live in the
 * middleware; Odoo's product Regulatory tab links here ("Manage documents
 * in SPI Ops") and shows one link per current document.
 */
export function ProductDocumentsPage() {
  const params = useParams({ strict: false }) as { tmplId?: string }
  const productId = params.tmplId && /^\d+$/.test(params.tmplId) ? Number(params.tmplId) : null
  const navigate = useNavigate()
  const canUpload = useHasAnyPermission([...UPLOAD_ROLES])
  const canRequest = useHasAnyPermission(DOC_REQUEST_CREATE_ROLES)
  const [requestOpen, setRequestOpen] = useState(false)

  const { data, isLoading, isError, error, refetch, isFetching } = useProductDocuments(productId)
  const { data: types = [] } = useDocumentTypes()
  const archive = useArchiveProductDocument()

  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadType, setUploadType] = useState<string | null>(null)
  const [uploadNonce, setUploadNonce] = useState(0)
  const [withdrawing, setWithdrawing] = useState<ProductDocument | null>(null)

  const openUpload = (typeKey: string | null) => {
    setUploadType(typeKey)
    setUploadNonce((n) => n + 1)
    setUploadOpen(true)
  }

  const currentColumns = useMemo<ColumnDef<ProductDocument, unknown>[]>(
    () => [
      { id: 'type', header: 'Document type', cell: (ctx) => ctx.row.original.doc_type_label },
      {
        id: 'file',
        header: 'File',
        cell: (ctx) => (
          <a
            className="inline-flex items-center gap-1 text-accent-text hover:underline"
            href={documentUrl(ctx.row.original.document_id, ctx.row.original.filename)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <FileText size={12} /> {ctx.row.original.filename}
          </a>
        ),
      },
      { id: 'version', header: 'Version', cell: (ctx) => ctx.row.original.version_label ?? '—' },
      { id: 'issued', header: 'Issued', cell: (ctx) => formatDate(ctx.row.original.issued_on) },
      { id: 'expires', header: 'Expires', cell: (ctx) => <ExpiryChip expiresOn={ctx.row.original.expires_on} /> },
      {
        id: 'uploaded',
        header: 'Uploaded',
        cell: (ctx) => (
          <div className="leading-snug">
            <div>{formatDate(ctx.row.original.created_at)}</div>
            <div className="text-sm text-secondary">{ctx.row.original.uploaded_by ?? '—'}</div>
          </div>
        ),
      },
      ...(canUpload
        ? [
            {
              id: 'actions',
              header: '',
              cell: (ctx) => (
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      openUpload(ctx.row.original.doc_type_key)
                    }}
                  >
                    Replace
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation()
                      setWithdrawing(ctx.row.original)
                    }}
                  >
                    Withdraw
                  </Button>
                </div>
              ),
            } satisfies ColumnDef<ProductDocument, unknown>,
          ]
        : []),
    ],
    [canUpload],
  )

  const historyColumns = useMemo<ColumnDef<ProductDocument, unknown>[]>(
    () => [
      { id: 'type', header: 'Document type', cell: (ctx) => ctx.row.original.doc_type_label },
      {
        id: 'file',
        header: 'File',
        cell: (ctx) => (
          <a
            className="text-accent-text hover:underline"
            href={documentUrl(ctx.row.original.document_id, ctx.row.original.filename)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ctx.row.original.filename}
          </a>
        ),
      },
      { id: 'version', header: 'Version', cell: (ctx) => ctx.row.original.version_label ?? '—' },
      {
        id: 'status',
        header: 'Status',
        cell: (ctx) => <Chip variant="neutral">{STATUS_LABEL[ctx.row.original.status]}</Chip>,
      },
      { id: 'replaced', header: 'Replaced', cell: (ctx) => formatDate(ctx.row.original.superseded_at) },
      { id: 'uploaded_by', header: 'Uploaded by', cell: (ctx) => ctx.row.original.uploaded_by ?? '—' },
    ],
    [],
  )

  if (productId == null) {
    return (
      <div className="p-6">
        <QueryErrorState error={new Error('Invalid product id in the URL')} subject="product documents" />
      </div>
    )
  }

  const product = data?.product
  const missing = data?.missing_required ?? []
  // A chip for every document on file, not only the missing required ones (Chris 2026-10-09).
  const chips = [
    ...documentChips([], data?.current ?? [], product?.spi_branded ?? false),
    ...missing.map((t) => ({ key: t.key, label: t.label, required: true, state: 'missing' as const })),
  ]

  return (
    <div data-density="comfortable" data-surface="form" className="flex h-full flex-col">
      <PageHeaderBand
        title={product ? product.name : 'Product documents'}
        description="Regulatory documents for this product. Odoo shows a link to each current document on the product's Regulatory tab."
      >
        <div className="ml-auto flex items-center gap-2">
          {product?.default_code ? <Chip variant="neutral">{product.default_code}</Chip> : null}
          {product ? (
            <Chip variant={product.spi_branded ? 'info' : 'neutral'}>
              {product.spi_branded ? 'SPI-branded' : 'Vendor product'}
            </Chip>
          ) : null}
          {product && !product.active ? <Chip variant="warning">Archived in Odoo</Chip> : null}
        </div>
      </PageHeaderBand>

      <Toolbar>
        <ToolbarLeft>
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/products' })}>
            <ArrowLeft size={14} /> All products
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(odooProductTemplateUrl(productId), '_blank', 'noopener')}
          >
            <ExternalLink size={14} /> Open in Odoo
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Refresh"
            loading={isFetching && !isLoading}
            onClick={() => void refetch()}
          >
            <RefreshCw size={14} />
          </Button>
          {canRequest ? (
            <Button variant="secondary" size="sm" onClick={() => setRequestOpen(true)} disabled={!product}>
              <FileUp size={14} /> Request from vendor
            </Button>
          ) : null}
          {canUpload ? (
            <Button variant="primary" size="sm" onClick={() => openUpload(null)} disabled={!product}>
              <Upload size={14} /> Upload document
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      {product ? (
        <RequestDocsDrawer
          open={requestOpen}
          onOpenChange={setRequestOpen}
          onOpenCard={(id) => void navigate({ to: '/regulatory/vendor/$requestId', params: { requestId: id } })}
          initialProducts={[{
            product_tmpl_id: productId, name: product.name, default_code: product.default_code,
            doc_types: missing.map((t) => t.key),
          }]}
        />
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto bg-canvas px-6 py-6">
        <div className="mx-auto flex w-full max-w-[calc(var(--form-content-width)+var(--form-aside-width))] flex-col gap-6">
          {isError ? (
            <QueryErrorState error={error} onRetry={() => void refetch()} subject="product documents" />
          ) : (
            <>
              <PanelCard title="Documents on file">
                <p className="text-md text-secondary">
                  {missing.length
                    ? `${missing.length} required ${missing.length === 1 ? 'document is' : 'documents are'} missing.${canUpload ? ' Click one to upload it.' : ''}`
                    : 'Every required document is on file.'}
                </p>
                <div className="flex flex-wrap gap-2" aria-label="Document status">
                  {chips.map((c) =>
                    c.state === 'missing' && canUpload ? (
                      <Button key={c.key} variant="secondary" size="sm" onClick={() => openUpload(c.key)}
                        className="border-warning-border bg-warning-bg text-warning-text">
                        <Upload size={14} /> {c.label} (required)
                      </Button>
                    ) : (
                      <Chip key={c.key} variant={c.state === 'done' ? 'success' : 'warning'} dot className="text-sm">
                        {c.label}{c.state === 'missing' ? ' (required)' : ''}
                      </Chip>
                    ),
                  )}
                </div>
              </PanelCard>

              <PanelCard title="Current documents">
                <DataTable
                  data={data?.current ?? []}
                  columns={currentColumns}
                  loading={isLoading}
                  getRowId={(r) => String(r.id)}
                  moduleId="products-current-docs"
                  stickyFirstColumn={false}
                  maxHeight="none"
                  errorSubject="product documents"
                />
              </PanelCard>

              {(data?.history.length ?? 0) > 0 ? (
                <PanelCard title="History">
                  <DataTable
                    data={data?.history ?? []}
                    columns={historyColumns}
                    getRowId={(r) => String(r.id)}
                    moduleId="products-doc-history"
                    stickyFirstColumn={false}
                    maxHeight="none"
                  />
                </PanelCard>
              ) : null}
            </>
          )}
        </div>
      </div>

      {product ? (
        <UploadDocumentDrawer
          key={uploadNonce}
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          productId={productId}
          productName={product.name}
          types={types}
          initialTypeKey={uploadType}
        />
      ) : null}

      <Modal
        open={withdrawing != null}
        onOpenChange={(open) => !open && setWithdrawing(null)}
        tone="warning"
        title={`Withdraw ${withdrawing?.doc_type_label ?? 'document'}?`}
        description="The document stays in history but is no longer current. Odoo's link is removed, and if this type is required the product shows it as missing until a new one is uploaded."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setWithdrawing(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={archive.isPending}
              onClick={() => {
                if (!withdrawing) return
                archive.mutate(withdrawing.id, {
                  onSuccess: () => {
                    toast.success(`${withdrawing.doc_type_label} withdrawn`)
                    setWithdrawing(null)
                  },
                  onError: (err) => toast.danger('Withdraw failed', { description: (err as Error).message }),
                })
              }}
            >
              Withdraw
            </Button>
          </div>
        }
      />
    </div>
  )
}
